import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import Input from "../forms/Input/Input";
import { Button } from "../forms/Buttons/Button";
import { createQuestionFolder, getQuestionFolders, updateQuestionFolder } from "../../services/questionService";
import { getApiErrorMessage } from "../../utils/apiError";

const errorText = (value) => [].concat(value).join("\n");

// Whether a folder (a row, or the answer to a save) comes from a server with «only approved questions in tests»: an
// old backend's folders have no approved_only_in_tests, and it ignores the field when it is sent.
const hasApprovedOnly = (folder) => !!folder && typeof folder === "object" && "approved_only_in_tests" in folder;

// For a new exam, whether the server has the rule, told by its folders: kept for the session once known. With no
// folders yet it is not known (and asked again next time); every dialog asking at once waits for the same request.
let approvedOnlyAvailable = null;
let probe = null;
const probeApprovedOnly = () => {
  if (!probe) {
    probe = getQuestionFolders().then(
      (list) => {
        probe = null;
        if (Array.isArray(list) && list.length) approvedOnlyAvailable = list.some(hasApprovedOnly);
        return approvedOnlyAvailable;
      },
      () => {
        probe = null;
        return null;
      }
    );
  }
  return probe;
};

// Whether to offer the rule: an exam being changed tells by its own row, a new one by the server's folders.
function useApprovedOnlyAvailable(folder) {
  const [available, setAvailable] = useState(approvedOnlyAvailable);
  useEffect(() => {
    if (folder) return undefined;
    // The answer may have come between this component's first render and this effect.
    if (approvedOnlyAvailable !== null) {
      setAvailable(approvedOnlyAvailable);
      return undefined;
    }
    let active = true;
    probeApprovedOnly().then((value) => {
      if (active) setAvailable(value);
    });
    return () => {
      active = false;
    };
  }, [folder]);
  return folder ? hasApprovedOnly(folder) : !!available;
}

// The toast once the dialog saved `saved` (the exam as the server answered): created, its test rule turned on
// or off, or renamed. `before` is the exam as it was (null for a new one). An answer without the rule (an old
// backend) did not change it.
export const examSavedMessage = (t, before, saved) => {
  if (!before) return t("folders.toast.exam_created");
  if (hasApprovedOnly(saved) && !!saved.approved_only_in_tests !== !!before.approved_only_in_tests) {
    const name = saved.name || before.name || "";
    return t(saved.approved_only_in_tests ? "folders.toast.approved_only_on" : "folders.toast.approved_only_off", { name });
  }
  return t("folders.toast.exam_renamed");
};

/**
 * An exam's settings (admins only): its name and «only approved questions in tests» (approved_only_in_tests:
 * the questions of its subjects go into tests only once approved; the subjects take the rule from the exam).
 * Creates an exam (`folder` = null) or changes the exam `folder`, sending only what changed. A 400 on a field
 * ({name: [...]}, {approved_only_in_tests: [...]}) is shown under it. The rule is offered only by a server that
 * has it, and one the answer does not show as stored is reported, not taken as saved.
 */
export default function FolderNameDialog({ isOpen, folder, onClose, onSaved }) {
  const [t] = useTranslation();
  const [name, setName] = useState("");
  const [onlyApproved, setOnlyApproved] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const flagAvailable = useApprovedOnlyAvailable(folder);

  useEffect(() => {
    if (isOpen) {
      setName(folder?.name || "");
      setOnlyApproved(!!folder?.approved_only_in_tests);
      setFieldErrors({});
      setError("");
    }
  }, [isOpen, folder]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setFieldErrors({ name: t("folders.dialog.name_required") });
      return;
    }
    const changes = {};
    if (folder && trimmed !== folder.name) changes.name = trimmed;
    if (folder && flagAvailable && onlyApproved !== !!folder.approved_only_in_tests) changes.approved_only_in_tests = onlyApproved;
    if (folder && !Object.keys(changes).length) {
      onClose();
      return;
    }
    // The rule as it is sent (undefined: not sent).
    const sentRule = folder ? changes.approved_only_in_tests : flagAvailable && onlyApproved ? true : undefined;
    setSaving(true);
    setFieldErrors({});
    setError("");
    try {
      const saved = folder
        ? await updateQuestionFolder(folder.uuid, changes)
        : await createQuestionFolder(trimmed, sentRule ? { approved_only_in_tests: true } : {});
      if (saved && typeof saved === "object") approvedOnlyAvailable = hasApprovedOnly(saved);
      // Sent, but the answer does not show it stored (a server without the rule ignores it).
      if (sentRule !== undefined && saved?.approved_only_in_tests !== sentRule) {
        if (folder && !changes.name) {
          // Nothing else was sent: nothing changed, the dialog stays.
          setFieldErrors({ flag: t("folders.exam_dialog.approved_only_not_saved") });
          return;
        }
        // The new exam or its new name is saved all the same.
        toast.error(t("folders.exam_dialog.approved_only_not_saved"));
      }
      onSaved(saved);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.name || data?.approved_only_in_tests) {
        setFieldErrors({
          name: data.name ? errorText(data.name) : "",
          flag: data.approved_only_in_tests ? errorText(data.approved_only_in_tests) : "",
        });
      } else {
        setError(getApiErrorMessage(t, err, "folders.toast.save_failed"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomDialog
      isOpen={isOpen}
      closeModal={() => !saving && onClose()}
      headerText={folder ? t("folders.exam_settings") : t("folders.exam_dialog.create_title")}
      maxWidth="max-w-md"
    >
      <form onSubmit={handleSubmit} className="mt-4 space-y-4" noValidate>
        <Input
          name="folder-name"
          id="folder-name"
          label={t("folders.exam_dialog.name_label")}
          placeholder={t("folders.exam_dialog.name_placeholder")}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (fieldErrors.name) setFieldErrors((prev) => ({ ...prev, name: "" }));
          }}
          errorMsg={fieldErrors.name}
          autoFocus
          autoComplete="off"
          aria-invalid={!!fieldErrors.name}
        />
        {flagAvailable && (
          <div>
            <label
              htmlFor="folder-approved-only"
              className="flex cursor-pointer select-none items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 transition duration-200 hover:border-slate-300"
            >
              <input
                id="folder-approved-only"
                type="checkbox"
                checked={onlyApproved}
                onChange={(e) => {
                  setOnlyApproved(e.target.checked);
                  if (fieldErrors.flag) setFieldErrors((prev) => ({ ...prev, flag: "" }));
                }}
                disabled={saving}
                aria-describedby="folder-approved-only-hint"
                aria-invalid={!!fieldErrors.flag}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-900">{t("folders.exam_dialog.approved_only_label")}</span>
                <span id="folder-approved-only-hint" className="mt-0.5 block text-sm text-slate-500">
                  {t("folders.exam_dialog.approved_only_hint")}
                </span>
              </span>
            </label>
            {fieldErrors.flag && (
              <p role="alert" className="mt-1.5 text-sm text-red-600">
                {fieldErrors.flag}
              </p>
            )}
          </div>
        )}
        {error && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" color="secondary-outline" text={t("buttons.cancel")} onClick={onClose} disabled={saving} className="max-sm:h-11" />
          <Button
            type="submit"
            color="primary"
            text={folder ? t("buttons.save") : t("buttons.create")}
            loading={saving}
            disabled={saving}
            className="max-sm:h-11"
          />
        </div>
      </form>
    </CustomDialog>
  );
}
